import React, { useState, useEffect } from 'react';
import SMSThread from '../SMSThread';
import APPThread from '../APPThread';
import EmailThread from '../EmailThread';
import SMSThreadSendBox from '../SMSThreadSendBox';
import { RecipientSelector } from '../RecipientSelector';
import { MessageSendBox } from '../MessageSendBox';
import { useInbox } from '../../contexts/inbox';
import { useMessages } from '../../contexts/messages';
import { InboxFishPane } from '../InboxFishPane';
import { getThreadFishId } from '../../utils/threadDisplay';
import { useStickToBottom } from '../../hooks/useStickToBottom';

const InboxChatArea = () => {
  const {
    isNewMessageOpen,
    selectedFishThread,
    handleFishSelectChange,
    handleAfterSend,
    inboxId,
    inbox,
    selectedChannel,
    markThreadRead,
  } = useInbox();

  const [recipients, setRecipients] = useState({
    mode: 'single',
    fishIds: [],
    segmentIds: [],
    tagIds: [],
    singleFish: undefined,
  });

  const { inboxes } = useMessages();

  // A recipient picked in the composer has no server-side thread yet, so it is
  // a draft rather than an EmailThread record to render and reply into.
  const emailThread =
    selectedChannel === 'EMAIL' && !selectedFishThread?.isDraft
      ? selectedFishThread
      : null;

  // Get the current inbox phone number
  const inboxPhoneNumber =
    inbox?.phoneNumber ||
    (selectedFishThread?.inboxId
      ? inboxes.find((i) => i.id === selectedFishThread.inboxId)?.phoneNumber
      : inboxes.find((i) => i.id === inboxId)?.phoneNumber) ||
    'Unknown';

  // The poll replaces selectedFishThread every few seconds, so scrolling keys
  // on the thread's identity and its message count, not on the object.
  const messageCount =
    (selectedFishThread?.messages?.length ?? 0) +
    (selectedFishThread?.SMSThread?.length ?? 0) +
    (emailThread?.inboundMessages?.length ?? 0) +
    (emailThread?.emailDeliveries?.length ?? 0);
  const threadRef = useStickToBottom(
    messageCount,
    selectedFishThread
      ? `${selectedChannel}:${selectedFishThread.inboxId}:${selectedFishThread.id}`
      : null,
  );

  // Handle recipient selection change - sync with existing fish select handler
  const handleRecipientsChange = (newRecipients) => {
    setRecipients(newRecipients);
    // If single mode and a fish is selected, also update the legacy handler
    if (newRecipients.mode === 'single' && newRecipients.singleFish) {
      handleFishSelectChange({
        value: newRecipients.singleFish,
        label: '',
      });
    }
  };

  // Reset recipients when new message is closed
  useEffect(() => {
    if (!isNewMessageOpen) {
      setRecipients({
        mode: 'single',
        fishIds: [],
        segmentIds: [],
        tagIds: [],
        singleFish: undefined,
      });
    }
  }, [isNewMessageOpen]);

  // Determine if using multi-channel send box (for new messages or non-SMS channel)
  const useMultiChannelSendBox = isNewMessageOpen || selectedChannel !== 'SMS';

  const fishId = selectedFishThread
    ? getThreadFishId(selectedFishThread, selectedChannel)
    : null;

  const threadList = selectedFishThread && (
    <div
      ref={threadRef}
      className="no-scrollbar min-h-0 flex-1 overflow-y-auto p-1 px-[20px]"
    >
      {selectedChannel === 'APP' &&
        // APP thread - uses messages array
        selectedFishThread?.messages?.length > 0 && (
          <APPThread
            messages={selectedFishThread.messages}
            currentInboxId={selectedFishThread.inboxId || inboxId}
            profiles={selectedFishThread.profiles}
          />
        )}
      {selectedChannel === 'SMS' &&
        // SMS thread - uses SMSThread array
        selectedFishThread?.SMSThread?.length > 0 && (
          <SMSThread
            SMSThread={selectedFishThread.SMSThread}
            fishPhoneNumber={selectedFishThread.phoneNumber}
            inboxPhoneNumber={inboxPhoneNumber}
          />
        )}
      {selectedChannel === 'EMAIL' && emailThread && (
        <EmailThread thread={emailThread} />
      )}
    </div>
  );

  return (
    <div className="relative flex min-h-0 flex-1 flex-col pb-2">
      {isNewMessageOpen && (
        <div className="mb-4 shrink-0 p-4">
          <RecipientSelector
            recipients={recipients}
            onRecipientsChange={handleRecipientsChange}
            showModeSelector={true}
            contactMode={selectedChannel}
          />
        </div>
      )}
      {selectedFishThread ? (
        <>
          {fishId ? (
            <InboxFishPane fishId={fishId} theme="light">
              {threadList}
            </InboxFishPane>
          ) : (
            threadList
          )}
          {/* Focusing the reply box or pressing Send reads the open thread.
              onClick covers browsers where clicking a button doesn't focus it. */}
          <div
            className="no-scrollbar shrink-0 bg-white"
            onFocus={() => markThreadRead(selectedFishThread)}
            onClick={() => markThreadRead(selectedFishThread)}
          >
            {selectedChannel === 'SMS' ? (
              <SMSThreadSendBox
                recipient={selectedFishThread.phoneNumber}
                fishId={selectedFishThread.id}
                senderId={selectedFishThread.inboxId}
                onAfterSend={handleAfterSend}
              />
            ) : emailThread && !emailThread.fishId ? (
              <p className="p-4 text-center text-sm text-gray-500">
                Replying to a staff conversation is not supported yet.
              </p>
            ) : (
              <MessageSendBox
                channel={selectedChannel}
                recipients={{
                  mode: 'single',
                  fishIds: [
                    // For EMAIL threads, the fish is a relation on the thread
                    // For APP threads, fish ID is in otherParticipant.fish.id
                    // For SMS threads, fish ID is in thread.id
                    emailThread?.fishId ||
                      selectedFishThread.otherParticipant?.fish?.id ||
                      selectedFishThread.id,
                  ],
                  singleFish:
                    emailThread?.fish ||
                    selectedFishThread.otherParticipant?.fish ||
                    selectedFishThread,
                }}
                fromInboxId={selectedFishThread.inboxId || inboxId}
                threadId={emailThread?.id}
                subject={emailThread?.subject}
                onAfterSend={handleAfterSend}
              />
            )}
          </div>
        </>
      ) : isNewMessageOpen ? (
        // New message mode - show send box for any recipient selection
        <>
          <div className="flex min-h-0 flex-1 items-center justify-center text-gray-500">
            {recipients.mode === 'single' && !recipients.singleFish
              ? 'Select a recipient to start messaging'
              : 'Compose a message to send to selected recipients'}
          </div>
          <div className="no-scrollbar shrink-0 bg-white">
            <MessageSendBox
              channel={selectedChannel}
              recipients={recipients}
              fromInboxId={inboxId}
              onAfterSend={handleAfterSend}
            />
          </div>
        </>
      ) : (
        <div className="flex flex-1 items-center justify-center text-gray-500">
          Select a conversation to start messaging
        </div>
      )}
    </div>
  );
};

export default InboxChatArea;
